import { useRouter } from 'next/router';
import { signOut } from 'supertokens-auth-react/recipe/session';
import { Settings } from '@/components/compound-components/settings';
import { Dialog } from '@/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu';
import {
  useCurrentUser,
  hasManagerOrAdminRole,
  hasAdminRole,
  useIsImpersonated,
  useImpersonation,
} from '@/lib/auth-service';
import UserAvatar from './user-avatar';
import { useEffect, useState } from 'react';
import {
  IS_HANDLE_CALLBACK_QUERY_PARAM,
  SOCIAL_PLATFORM_QUERY_PARAM,
} from '@/consts/social-media';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useOrganizationContext } from '@/contexts/organization-context';
import { logInfo } from '@/lib/rum';
import {
  ChevronDownIcon,
  ExitIcon,
  GearIcon,
  HomeIcon,
  ListBulletIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import Text from '@/components/basic/text';
import { env } from 'next-runtime-env';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import googleIcon from '@/assets/icons/post-google.svg';
import Image from 'next/image';
import { Button } from '@/components/shadcn/button';
import { RoutePaths } from '@/consts/routes';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { toast } from '@/components/shadcn/use-toast';

const WEBSITE_DOMAIN = env('WEBSITE');

export function UserAvatarMenu() {
  const { user, isLoading } = useCurrentUser();
  const { organizationId, organizationList, organizationLogos } =
    useOrganizationContext();
  const organizationLogo = organizationLogos && organizationLogos[0]?.url;
  const session = useSessionContext();
  const router = useRouter();
  const [open, setIsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isLargeDesktop = useMediaQuery('(min-width: 1024px)');
  const isGsuiteIntegrationEnabled =
    !!useFeatureFlagEnabled('gsuite-integration');
  const organizationName = organizationList?.find(
    (item) => item.id === organizationId
  )?.name;
  const isAdmin =
    !session.loading &&
    session.doesSessionExist &&
    hasAdminRole(session.accessTokenPayload['st-role']?.v);
  const isManagerOrAdmin =
    !session.loading &&
    session.doesSessionExist &&
    hasManagerOrAdminRole(session.accessTokenPayload['st-role']?.v);
  const isWorkspaceEnabled = useFeatureFlagEnabled('workspace');
  const isImpersonated = useIsImpersonated();
  const { revertImpersonation } = useImpersonation(organizationId as string);

  useEffect(() => {
    logInfo('User avatar menu', {
      query: router.query,
    });
    if (router.query) {
      const isHandleCallback = router.query[IS_HANDLE_CALLBACK_QUERY_PARAM];
      const socialMediaPlatform = router.query[SOCIAL_PLATFORM_QUERY_PARAM];
      const settingsTab = router.query['settingstab'];

      if (
        (isHandleCallback && socialMediaPlatform && !isMobile) ||
        settingsTab
      ) {
        setIsOpen(true);
      }
    }
  }, [router.query, isMobile]);

  if (isLoading) {
    return null;
  }

  const handleRevertImpersonation = async () => {
    try {
      await revertImpersonation();
      toast({
        title: 'Success',
        description: 'Successfully reverted impersonation',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to revert impersonation',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className='mt-2 flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-3 py-2 hover:cursor-pointer hover:border hover:border-neutral-600 hover:bg-neutral-100 data-[state=open]:border data-[state=open]:border-vee lg:min-w-44'>
            <div className='flex items-center gap-2'>
              {organizationName ? (
                <UserAvatar
                  className='h-6 w-6'
                  imgClassName='object-cover'
                  username={organizationName}
                  imgSrc={organizationLogo}
                />
              ) : (
                <div className='h-6 w-6 rounded-full bg-neutral-50' />
              )}
              {isLargeDesktop && (
                <Text
                  className='text-primary-600'
                  weight='semibold'
                  variant='body1'
                >
                  {organizationName}
                </Text>
              )}
              {isImpersonated && (
                <span className='rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800'>
                  Impersonating
                </span>
              )}
            </div>

            <ChevronDownIcon className='h-5 w-5 text-neutral-600 hover:text-neutral-800' />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='mr-12 w-[22rem] max-w-full px-2 py-2'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3 p-2'>
              {organizationName ? (
                <UserAvatar
                  imgClassName='object-cover'
                  className='h-12 w-12'
                  username={organizationName}
                  imgSrc={organizationLogo}
                />
              ) : (
                <div className='h-12 w-12 rounded-full bg-neutral-50' />
              )}
              <Text
                className='text-primary-600'
                weight='semibold'
                variant='body2'
              >
                {organizationName}
              </Text>
            </div>

            {isWorkspaceEnabled && (
              <DropdownMenuItem
                className='px-0 hover:!bg-transparent'
                onClick={() => {
                  router.push(
                    `${RoutePaths.Workspace}?organizationId=${organizationId}`
                  );
                }}
              >
                <Button className='mr-2 h-8 w-8 p-0' variant={'secondary'}>
                  <GearIcon />
                </Button>
              </DropdownMenuItem>
            )}
          </div>
          <DropdownMenuItem
            className='flex cursor-pointer items-center gap-3 px-2 text-primary-600'
            onClick={() =>
              router.replace(
                `/${organizationId ? `?organizationId=${organizationId}` : ''}`
              )
            }
          >
            <div className='rounded-lg border border-primary-200 p-2'>
              <HomeIcon />{' '}
            </div>
            Home
          </DropdownMenuItem>
          <DropdownMenuItem
            className='flex cursor-pointer items-center gap-3 px-2 text-primary-600'
            onClick={() => setIsOpen(true)}
          >
            <div className='rounded-lg border border-primary-200 p-2'>
              <GearIcon />
            </div>
            Settings
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem
              onClick={() => router.push(`${WEBSITE_DOMAIN}/admin/crawled`)}
              className='flex cursor-pointer items-center gap-3 px-2 text-primary-600'
            >
              <div className='rounded-lg border border-primary-200 p-2'>
                <ListBulletIcon />
              </div>
              Pending Grants
            </DropdownMenuItem>
          )}
          {isManagerOrAdmin && isGsuiteIntegrationEnabled && (
            <DropdownMenuItem
              className='flex w-full cursor-pointer items-center gap-[10px] text-primary-600'
              onClick={() =>
                router.push(
                  `${WEBSITE_DOMAIN}/gsuite-content?organizationId=${organizationId}`
                )
              }
            >
              <div className='rounded-lg border border-primary-200 p-2'>
                <Image
                  src={googleIcon}
                  width={18}
                  height={18}
                  alt='Google Account Content page'
                />
              </div>
              Google Account Content
            </DropdownMenuItem>
          )}
          {isImpersonated && (
            <>
              <DropdownMenuItem
                className='flex cursor-pointer items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2'
                onClick={handleRevertImpersonation}
              >
                <PersonIcon className='text-yellow-600' />
                <Text className='text-yellow-800'>Stop Impersonating</Text>
              </DropdownMenuItem>
              <DropdownMenuSeparator className='my-2' />
            </>
          )}
          <DropdownMenuSeparator className='mx-2 mt-2' />
          <div className='flex items-center justify-between px-2 pb-3 pt-4'>
            <div className='flex items-center gap-3'>
              <UserAvatar
                className='h-6 w-6 text-sm'
                username={user.primaryEmail}
                imgSrc=''
              />
              <Text
                className='max-w-44 truncate text-primary-600'
                variant='body2'
              >
                {user.metadata.firstName
                  ? `${user.metadata.firstName} ${user.metadata.lastName}`
                  : user.primaryEmail}
              </Text>
            </div>
            <DropdownMenuItem
              className='flex cursor-pointer gap-2 rounded-lg border border-primary-200'
              onClick={async () => await signOut()}
            >
              <ExitIcon />{' '}
              <Text
                weight='medium'
                variant='body2'
                className='text-primary-600'
              >
                Logout
              </Text>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <Settings />
    </Dialog>
  );
}
